import React, { FC, useEffect, useRef, useState } from "react";
import {
    Badge,
    Card,
    Collapse,
    FormControl,
    InputGroup
} from "react-bootstrap";
import { MutatorProperty } from "../../../models/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { useMutatorState } from "../../../graphql/hooks";
import styles from "./CustomTypeCard.module.scss";
import cx from "classnames";

interface Props {
    fieldProperty: MutatorProperty;
    typeProperty: MutatorProperty;
    getVariantByType: (type: string) => string;
    onChange: (name: string, value: number) => void;
}

const MutatorNumberFieldCardImplementation: FC<Props> = ({
    fieldProperty,
    typeProperty,
    getVariantByType,
    onChange
}) => {
    const inputRef = useRef(null);
    const mutatorState = useMutatorState();
    const [value, updateValue] = useState(fieldProperty.value);
    const [showMeta, updateShowMeta] = useState(mutatorState.showMeta);
    const [notification, updateNotification] = useState<string | undefined>(
        undefined
    );

    useEffect(() => {
        if (showMeta !== mutatorState.showMeta) {
            updateShowMeta(mutatorState.showMeta);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mutatorState.showMeta]);

    useEffect(() => {
        if (fieldProperty.value && fieldProperty.value !== value) {
            updateValue(fieldProperty.value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldProperty.value]);

    const forceClickBehaviour = () => {
        // Some browers do not focus the form properly when clicking increase / decrease
        if (inputRef.current) {
            // @ts-ignore
            inputRef.current.focus();
        }
    };

    return (
        <Card className="text-inverse inner-card">
            <Card.Header
                className={`d-flex align-items-center ${styles.cardHeader}`}
            >
                {fieldProperty.name}
                <Badge
                    className="mx-2"
                    bg={getVariantByType(fieldProperty.type)}
                >
                    {fieldProperty.type}
                </Badge>
                {typeProperty.meta ? (
                    <div
                        className="float-end ms-auto mouseHover"
                        onClick={() => updateShowMeta(!showMeta)}
                        title="Help and additional info"
                    >
                        <FontAwesomeIcon
                            icon={faCircleInfo}
                            className={cx({
                                "text-inverse-25": !showMeta,
                                "text-inverse-50": showMeta
                            })}
                        />
                    </div>
                ) : null}
            </Card.Header>
            <Card.Body className={`${styles.cardBody}`}>
                <InputGroup>
                    <InputGroup.Text>Value</InputGroup.Text>
                    <FormControl
                        ref={inputRef}
                        type="number"
                        value={value}
                        onClick={forceClickBehaviour}
                        onChange={e => {
                            const newValueAsNumber = Number(e.target.value);
                            const trimmedInput = e.target.value.trim();
                            if (
                                // @ts-ignore
                                !(
                                    !isNaN(parseFloat(trimmedInput)) &&
                                    // @ts-ignore
                                    isFinite(trimmedInput)
                                )
                            ) {
                                updateNotification("Valid number is required");
                            } else if (
                                typeProperty.meta?.min &&
                                newValueAsNumber < typeProperty.meta.min
                            ) {
                                updateNotification(
                                    `Minimum allowed value ${typeProperty.meta.min}`
                                );
                            } else if (
                                typeProperty.meta?.max &&
                                newValueAsNumber > typeProperty.meta.max
                            ) {
                                updateNotification(
                                    `Maximum allowed value ${typeProperty.meta.max}`
                                );
                            } else {
                                updateNotification(undefined);
                            }
                            updateValue(e.target.value);
                        }}
                        onBlur={e => {
                            const newValue = Number(e.target.value);
                            const trimmedInput = e.target.value.trim();
                            if (
                                // @ts-ignore
                                !(
                                    !isNaN(parseFloat(trimmedInput)) &&
                                    // @ts-ignore
                                    isFinite(trimmedInput)
                                )
                            ) {
                                updateNotification("Valid number is required");
                                return;
                            } else if (
                                typeProperty.meta?.min &&
                                newValue < typeProperty.meta.min
                            ) {
                                updateNotification(
                                    `Minimum allowed value ${typeProperty.meta.min}`
                                );
                                return;
                            } else if (
                                typeProperty.meta?.max &&
                                newValue > typeProperty.meta.max
                            ) {
                                updateNotification(
                                    `Maximum allowed value ${typeProperty.meta.max}`
                                );
                                return;
                            } else {
                                updateNotification(undefined);
                            }
                            onChange(typeProperty.name, newValue);
                        }}
                    />
                </InputGroup>
            </Card.Body>
            {typeProperty.meta ? (
                <Collapse in={showMeta}>
                    <div>
                        <Card.Footer className={styles.footerInfo}>
                            {typeProperty.meta.description ? (
                                <div className="medium-font text-inverse-50">
                                    {typeProperty.meta.description}
                                </div>
                            ) : (
                                <span className="medium-font text-inverse-50">
                                    No description provided.
                                </span>
                            )}
                            {typeProperty.meta.min ? (
                                <div className="medium-font text-info">
                                    <span>Min: {typeProperty.meta.min}</span>
                                </div>
                            ) : null}
                            {typeProperty.meta.max ? (
                                <div className="medium-font text-info">
                                    <span>Max: {typeProperty.meta.max}</span>
                                </div>
                            ) : null}
                        </Card.Footer>
                    </div>
                </Collapse>
            ) : null}
            {notification ? (
                <Card.Footer className={styles.footerInfo}>
                    <div className="medium-font text-danger">
                        {notification}
                    </div>
                </Card.Footer>
            ) : null}
        </Card>
    );
};

export const MutatorNumberFieldCard = React.memo(
    MutatorNumberFieldCardImplementation
);
